<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { session } from '../session'
import { formatBytes, formatDate, formatDuration, formatPercent, runKind, runOutcome, auditEventLabel } from '../monitoring'

const { locale, t } = useI18n()
const isAdmin = computed(() => session.user?.role === 'ADMIN')
const metrics = ref(null)
const auditPreview = ref([])
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const auditSettings = ref(null)
const auditSaving = ref(false)
const auditClearing = ref(false)
const auditError = ref('')
const auditNotice = ref('')
let refreshTimer = null

const processMetrics = computed(() => metrics.value?.process ?? null)
const reportMetrics = computed(() => metrics.value?.reports ?? null)
const recentRuns = computed(() => reportMetrics.value?.recent?.slice(0, 5) ?? [])
const activeRuns = computed(() => reportMetrics.value?.active ?? [])
const importantAuditPreview = computed(() => auditPreview.value
  .filter((event) => !['catalog_listed', 'catalog_item_viewed'].includes(event.eventType))
  .slice(0, 5))

function progressValue(value, maximum) {
  if (!maximum || value == null) return 0
  return Math.min(100, Math.max(0, (Number(value) / Number(maximum)) * 100))
}

async function load(initial = false) {
  if (refreshing.value) return
  if (initial) loading.value = true
  refreshing.value = true
  error.value = ''
  try {
    const [metricsResult, auditResult] = await Promise.all([
      api('/admin/metrics'),
      api('/admin/audit-events?page=1&pageSize=25'),
    ])
    metrics.value = metricsResult
    auditPreview.value = auditResult.events
  } catch (cause) {
    error.value = cause.message || t('metricsUnavailable')
  } finally {
    refreshing.value = false
    loading.value = false
  }
}

async function loadAuditSettings() {
  try {
    auditSettings.value = await api('/admin/audit-settings')
  } catch (cause) {
    auditError.value = cause.message || t('auditSettingsFailed')
  }
}

async function saveAuditRetention() {
  if (!auditSettings.value) return
  auditError.value = ''
  auditNotice.value = ''
  auditSaving.value = true
  try {
    const result = await api('/admin/audit-settings', {
      method: 'PUT', body: JSON.stringify({ retentionDays: auditSettings.value.retentionDays }),
    })
    auditSettings.value = result
    auditNotice.value = t('auditRetentionSaved', { count: result.purgedCount })
    await load()
  } catch (cause) {
    auditError.value = cause.message || t('auditSettingsFailed')
  } finally {
    auditSaving.value = false
  }
}

async function clearAuditLog() {
  if (!window.confirm(t('clearAuditConfirm'))) return
  auditError.value = ''
  auditNotice.value = ''
  auditClearing.value = true
  try {
    const result = await api('/admin/audit-events', {
      method: 'DELETE', body: JSON.stringify({ confirmation: 'PERMANENTLY_DELETE_AUDIT_EVENTS' }),
    })
    if (auditSettings.value) auditSettings.value.eventCount = 0
    auditPreview.value = []
    auditNotice.value = t('auditLogCleared', { count: result.deletedCount })
  } catch (cause) {
    auditError.value = cause.message || t('auditSettingsFailed')
  } finally {
    auditClearing.value = false
  }
}

onMounted(async () => {
  await Promise.all([load(true), loadAuditSettings()])
  refreshTimer = window.setInterval(() => load(), 5000)
})

onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer)
})
</script>

<template>
  <section class="monitoring-console">
    <div class="console-header">
      <div>
        <p class="eyebrow">{{ t('liveOperations') }}</p>
        <div class="console-title-row"><h1>{{ t('monitoringTitle') }}</h1><span class="health-status">{{ t('healthy') }}</span></div>
        <p class="muted console-updated">{{ metrics ? t('updatedAt', { value: formatDate(locale, metrics.sampledAt) }) : t('monitoringHint') }}</p>
      </div>
      <button class="button button-small" type="button" :disabled="refreshing" @click="load()">
        {{ refreshing ? t('refreshing') : t('refresh') }}
      </button>
    </div>

    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="!isAdmin" class="notice info" role="status">{{ t('viewOnlyHint') }}</p>
    <p v-if="loading" class="muted" aria-live="polite">{{ t('loading') }}</p>

    <template v-else-if="processMetrics && reportMetrics">
      <div class="operations-grid">
        <section class="card operation-surface">
          <div class="section-heading"><div><h2>{{ t('currentRuns') }}</h2></div><span class="status">{{ reportMetrics.activeCount }}</span></div>
          <div v-if="!activeRuns.length" class="operation-empty">
            <strong>{{ t('currentRunsEmpty') }}</strong>
            <p class="muted">{{ t('currentRunsEmptyHint') }}</p>
          </div>
          <div v-else class="active-run-list">
            <article v-for="run in activeRuns" :key="run.id" class="active-run">
              <div><strong>{{ run.templateName }}</strong><span>{{ runKind(t, run.kind) }} · {{ t('activeFor', { duration: formatDuration(run.elapsedMs) }) }}</span></div>
              <span>{{ formatDate(locale, run.startedAt) }}</span>
            </article>
          </div>
          <div class="operation-footer">
            <div><strong>{{ t('queueRuns', { count: 0 }) }}</strong><small>{{ t('waitingForRun') }}</small></div>
            <RouterLink class="text-action" :to="{ name: 'admin-run-history' }">{{ t('runHistory') }} →</RouterLink>
          </div>
        </section>

        <section class="card process-surface" :aria-label="t('processState')">
          <div class="section-heading"><h2>{{ t('processState') }}</h2></div>
          <div class="process-row"><span>{{ t('processCpu') }}</span><strong>{{ formatPercent(processMetrics.processCpuLoadPercent) }}</strong><progress :value="processMetrics.processCpuLoadPercent ?? 0" max="100" /><small>{{ t('systemCpu', { value: formatPercent(processMetrics.systemCpuLoadPercent) }) }}</small></div>
          <div class="process-row"><span>{{ t('heapMemory') }}</span><strong>{{ formatBytes(processMetrics.heapUsedBytes) }}</strong><progress :value="progressValue(processMetrics.heapUsedBytes, processMetrics.heapMaxBytes)" max="100" /><small>{{ t('ofMaximum', { value: formatBytes(processMetrics.heapMaxBytes) }) }}</small></div>
          <div class="process-row compact"><span>{{ t('threads') }}</span><strong>{{ processMetrics.threadCount }}</strong><small>{{ t('peakThreads', { value: processMetrics.peakThreadCount }) }}</small></div>
          <div class="process-row compact"><span>{{ t('uptime') }}</span><strong>{{ formatDuration(processMetrics.uptimeMs) }}</strong><small>{{ t('activeRunsCount', { count: reportMetrics.activeCount }) }}</small></div>
        </section>
      </div>

      <section class="card console-section recent-runs-panel">
        <div class="section-heading"><div><h2>{{ t('recentRuns') }}</h2><p class="muted">{{ t('recentRunsHint') }}</p></div><span class="muted">{{ t('completedRuns', { count: reportMetrics.completedCount }) }} · {{ t('failedRuns', { count: reportMetrics.failedCount }) }}</span></div>
        <p v-if="!recentRuns.length" class="muted">{{ t('noRecentRuns') }}</p>
        <div v-else class="table-wrap"><table><thead><tr><th>{{ t('template') }}</th><th>{{ t('type') }}</th><th>{{ t('started') }}</th><th>{{ t('duration') }}</th><th>{{ t('resultSize') }}</th><th>{{ t('heapChange') }}</th><th>{{ t('outcome') }}</th></tr></thead><tbody>
          <tr v-for="run in recentRuns" :key="run.id"><td><strong>{{ run.templateName }}</strong></td><td>{{ runKind(t, run.kind) }}</td><td>{{ formatDate(locale, run.startedAt) }}</td><td>{{ formatDuration(run.durationMs) }}</td><td>{{ formatBytes(run.resultBytes) }}</td><td>{{ formatBytes(run.heapDeltaBytes, true) }}</td><td><span class="run-state" :class="run.status">{{ runOutcome(t, run) }}<small v-if="run.errorCode">{{ run.errorCode }}</small></span></td></tr>
        </tbody></table></div>
        <RouterLink class="panel-action" :to="{ name: 'admin-run-history' }">{{ t('viewAllRuns') }}</RouterLink>
      </section>

      <section v-if="auditSettings" class="card console-section audit-preview-panel">
        <div class="section-heading"><div><h2>{{ t('auditLogTitle') }}</h2><p class="muted">{{ t('auditPreviewHint') }}</p></div><span class="muted">{{ t('auditEventCount', { count: auditSettings.eventCount }) }}</span></div>
        <p v-if="!importantAuditPreview.length" class="muted">{{ t('noAuditEvents') }}</p>
        <div v-else class="table-wrap audit-preview-table"><table><thead><tr><th>{{ t('started') }}</th><th>{{ t('auditActor') }}</th><th>{{ t('auditAction') }}</th><th>{{ t('auditDetails') }}</th></tr></thead><tbody>
          <tr v-for="event in importantAuditPreview" :key="event.id"><td>{{ formatDate(locale, event.createdAt) }}</td><td>{{ event.actor }}</td><td><strong>{{ auditEventLabel(t, event.eventType) }}</strong></td><td>{{ event.detail || '—' }}</td></tr>
        </tbody></table></div>
        <RouterLink class="panel-action" :to="{ name: 'admin-audit-log' }">{{ t('viewAllActions') }}</RouterLink>
      </section>

      <section v-if="isAdmin && auditSettings" class="audit-controls monitoring-maintenance">
        <form class="card audit-retention-form" @submit.prevent="saveAuditRetention">
          <label>{{ t('auditRetentionDays') }}<input v-model.number="auditSettings.retentionDays" type="number" min="1" max="3650" required /></label>
          <div><p class="muted">{{ t('auditRetentionHint') }}</p><button class="button button-small" type="submit" :disabled="auditSaving || auditClearing">{{ auditSaving ? t('savingSettings') : t('saveSettings') }}</button></div>
        </form>
        <aside class="audit-danger-zone"><strong>{{ t('permanentClear') }}</strong><p>{{ t('permanentClearHint') }}</p><button class="button button-small button-danger" type="button" :disabled="auditSaving || auditClearing" @click="clearAuditLog">{{ auditClearing ? t('clearingAudit') : t('permanentClear') }}</button></aside>
      </section>
      <p v-if="auditError" class="notice error" role="alert">{{ auditError }}</p>
      <p v-if="auditNotice" class="notice success">{{ auditNotice }}</p>
    </template>
  </section>
</template>
